import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import QRCode from 'qrcode'
import type { AxiosError } from 'axios'

/** Veri Merkezi sekmesindeki bölümlerin ortak parçaları */

export function Section({ title, description, actions, children }: {
  title: string; description?: string; actions?: ReactNode; children: ReactNode
}): JSX.Element {
  return (
    <div className="glass-card overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-border/50 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export function InfoRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right text-foreground ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</span>
    </div>
  )
}

export function CopyButton({ value, label }: { value: string; label?: string }): JSX.Element {
  const { t } = useTranslation('datacenter')
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="btn-secondary flex items-center gap-1.5 text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? t('common.copied') : label ?? t('common.copy')}
    </button>
  )
}

/** QR: modül başına TAM sayıda piksel (kesirli ölçekleme kenarları bulandırır, telefon kamerası okuyamaz) ve keskin çizim */
export function QrImage({ value, size = 180 }: { value: string; size?: number }): JSX.Element | null {
  const [qr, setQr] = useState<{ url: string; px: number } | null>(null)
  useEffect(() => {
    let alive = true
    const margin = 4
    const modules = QRCode.create(value, { errorCorrectionLevel: 'L' }).modules.size + margin * 2
    const scale = Math.max(2, Math.round(size / modules))
    void QRCode.toDataURL(value, { scale, margin, errorCorrectionLevel: 'L' }).then((url) => { if (alive) setQr({ url, px: modules * scale }) })
    return () => { alive = false }
  }, [value, size])
  return qr ? <img src={qr.url} alt="" width={qr.px} height={qr.px} style={{ imageRendering: 'pixelated' }} className="rounded-md bg-white p-1" /> : null
}

export function formatBytes(n: number): string {
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`
  return `${(n / 1024 ** 3).toFixed(1)} GB`
}

export function formatDateTime(iso: string | null, locale: string): string {
  return iso ? new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : 'tr-TR', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
}

/** API hata mesajı (backend `{ error: { message } }`) */
export function apiError(e: unknown): string {
  const err = e as AxiosError<{ error?: { message?: string } }>
  return err.response?.data?.error?.message ?? (e instanceof Error ? e.message : String(e))
}

export function Toggle({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean
}): JSX.Element {
  return (
    <label className={`flex items-start gap-3 py-2 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block text-sm text-foreground">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  )
}
